import React, { useState, useEffect, useRef, useCallback } from "react";
import { Save, ChevronDown, Bot, Database, Eye } from "lucide-react";
import ApiClient from "../utils/api";
import PageHeader from "../components/PageHeader";
import { useToast } from "../context/ToastContext";

const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div ref={dropdownRef} className="relative">
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl text-xs bg-gray-50 ${disabled
          ? "opacity-50 cursor-not-allowed"
          : "cursor-pointer hover:bg-gray-100"
          } flex justify-between items-center transition-all`}
      >
        <span className={selectedOption ? "text-gray-900" : "text-gray-400"}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`transition-transform text-gray-600 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl mt-1 max-h-60 overflow-y-auto z-50 shadow-lg">
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`px-4 py-3 text-sm cursor-pointer hover:bg-gray-50 ${value === option.value
                ? "bg-gray-50 text-gray-900"
                : "text-gray-700"
                }`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const UNCHANGED_KEY = "__UNCHANGED__";

const CHAT_MODELS = [
  // OpenAI
  { value: "gpt-4o", label: "GPT-4o (Multimodal)", provider: "openai" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini (Cost-Efficient)", provider: "openai" },
  { value: "gpt-4", label: "GPT-4 (Reliable Capable)", provider: "openai" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo (Fast)", provider: "openai" },
  { value: "gpt-4.1", label: "GPT-4.1 (Non-Reasoning)", provider: "openai" },
  { value: "gpt-5-mini", label: "GPT-5 Mini (Fast & Efficient)", provider: "openai" },
  { value: "gpt-5-nano", label: "GPT-5 Nano (Ultra Fast)", provider: "openai" },
  { value: "gpt-5.1", label: "GPT-5.1 (Best for Agents)", provider: "openai" },
  { value: "o1-preview", label: "o1 Preview (Reasoning)", provider: "openai" },
  { value: "o1-mini", label: "o1 Mini (Fast Reasoning)", provider: "openai" },
  { value: "o3-mini", label: "o3-mini (Best for Coding)", provider: "openai" },
  { value: "gpt-oss-120b", label: "GPT OSS 120B (Open-Weight)", provider: "openai" },
  { value: "gpt-oss-20b", label: "GPT OSS 20B (High-Reasoning)", provider: "openai" },

  // Anthropic
  { value: "claude-3-5-sonnet-20240620", label: "Claude 3.5 Sonnet", provider: "anthropic" },
  { value: "claude-3-5-haiku-20241022", label: "Claude 3.5 Haiku", provider: "anthropic" },
  { value: "claude-3-haiku-20240307", label: "Claude 3 Haiku", provider: "anthropic" },
  { value: "claude-3-opus-20240229", label: "Claude 3 Opus", provider: "anthropic" },
  { value: "claude-4.1-opus", label: "Claude 4.1 Opus (Research)", provider: "anthropic" },
  { value: "claude-4.5-haiku", label: "Claude 4.5 Haiku (Instant)", provider: "anthropic" },
  { value: "claude-4.5-sonnet-20240620", label: "Claude 4.5 Sonnet (Coding)", provider: "anthropic" },

  // Google
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro (Advanced)", provider: "google" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash (Balanced)", provider: "google" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash-Lite (Fast)", provider: "google" },
  { value: "gemini-2.0-flash", label: "Gemini 2.0 Flash", provider: "google" },
  { value: "gemini-2.0-flash-lite", label: "Gemini 2.0 Flash-Lite", provider: "google" },
  { value: "gemini-pro-latest", label: "Gemini 1.5 Pro (Latest)", provider: "google" },
  { value: "gemini-flash-latest", label: "Gemini 1.5 Flash (Latest)", provider: "google" },
  { value: "gemini-pro", label: "Gemini Pro", provider: "google" },

  // Meta
  { value: "llama-3.3-70b-instruct", label: "Llama 3.3 70B (Text Only)", provider: "meta" },
  { value: "llama-3.1-405b-instruct", label: "Llama 3.1 405B (Massive)", provider: "meta" },
  { value: "llama-3.1-70b-instruct", label: "Llama 3.1 70B", provider: "meta" },
  { value: "llama-3.1-8b-instruct", label: "Llama 3.1 8B", provider: "meta" },
  { value: "llama-3.2-3b-instruct", label: "Llama 3.2 3B", provider: "meta" },
  { value: "llama-3.2-1b-instruct", label: "Llama 3.2 1B", provider: "meta" },
  { value: "Llama-4-Maverick-17B-128E-Instruct-FP8", label: "Llama 4 Maverick 17B", provider: "meta" },
  { value: "Llama-4-Scout-17B-16E-Instruct", label: "Llama 4 Scout 17B", provider: "meta" },

  // Mistral
  { value: "mistral-large-latest", label: "Mistral Large (Latest)", provider: "mistral" },
  { value: "mistral-large-2", label: "Mistral Large 2", provider: "mistral" },
  { value: "mistral-medium-3", label: "Mistral Medium 3", provider: "mistral" },
  { value: "mistral-small-latest", label: "Mistral Small", provider: "mistral" },
  { value: "mistral-small-3.1", label: "Mistral Small 3.1", provider: "mistral" },
  { value: "codestral-latest", label: "Codestral (Coding)", provider: "mistral" },
  { value: "mixtral-8x22b-instruct", label: "Mixtral 8x22B", provider: "mistral" },
];

const VISION_MODELS = [
  // OpenAI
  { value: "gpt-4o", label: "GPT-4o (Multimodal)", provider: "openai" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini (Cost-Efficient)", provider: "openai" },
  // Google
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash (Balanced)", provider: "google" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro (Advanced)", provider: "google" },
  { value: "gemini-flash-latest", label: "Gemini 1.5 Flash (Latest)", provider: "google" },
  { value: "gemini-pro-latest", label: "Gemini 1.5 Pro (Latest)", provider: "google" },
];

const EMBEDDERS = [
  // OpenAI
  { value: "text-embedding-3-small", label: "Text Embedding 3 Small", provider: "openai" },
  { value: "text-embedding-3-large", label: "Text Embedding 3 Large", provider: "openai" },
  { value: "text-embedding-ada-002", label: "ADA-002 (Legacy)", provider: "openai" },

  // Google
  { value: "gemini-embedding-2", label: "Gemini Embedding 2 (Recommended)", provider: "google" },
  { value: "gemini-embedding-001", label: "Gemini Embedding 001", provider: "google" },

  // Cohere
  { value: "embed-english-v3.0", label: "Cohere English v3", provider: "cohere" },
  { value: "embed-multilingual-v3.0", label: "Cohere Multilingual v3", provider: "cohere" },

  // Others from legacy
  { value: "mistral-embed", label: "Mistral Embed", provider: "mistral" },
  { value: "jina-embeddings-v3", label: "Jina Embeddings v3", provider: "jina" },
  { value: "nomic-ai/nomic-embed-text-v1.5", label: "Nomic Text v1.5 (Fireworks)", provider: "fireworks" },
  { value: "text-search-doc/latest", label: "Nebius Search Doc", provider: "nebius" },
  { value: "text-search-query/latest", label: "Nebius Search Query", provider: "nebius" },
  { value: "togethercomputer/m2-bert-80M-32k-retrieval", label: "Together M2 BERT 32k", provider: "together" },
  { value: "togethercomputer/m2-bert-80M-8k-retrieval", label: "Together M2 BERT 8k", provider: "together" },
];

const ModelManagement = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("chat-models");

  // Chat Models State
  const [selectedChatProvider, setSelectedChatProvider] = useState("");
  const [selectedChatModel, setSelectedChatModel] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  // Vision Models State
  const [selectedVisionProvider, setSelectedVisionProvider] = useState("");
  const [selectedVisionModel, setSelectedVisionModel] = useState("");
  const [visionLoading, setVisionLoading] = useState(false);

  // Embedders State
  const [selectedEmbedderProvider, setSelectedEmbedderProvider] = useState("");
  const [selectedEmbedderModel, setSelectedEmbedderModel] = useState("");
  const [embedderLoading, setEmbedderLoading] = useState(false);


  const fetchActiveModel = useCallback(async () => {
    try {
      const data = await ApiClient.request("/models/active");
      if (data) {
        setSelectedChatProvider(data.provider);
        setSelectedChatModel(data.model_id);
        setSelectedVisionProvider(data.vision_provider || "");
        setSelectedVisionModel(data.vision_model_id || "");
      }
    } catch (error) {
      console.error("Error fetching active model:", error);
    }
  }, []);

  const fetchActiveEmbedder = useCallback(async () => {
    try {
      const data = await ApiClient.request("/embedder/active");
      if (data) {
        setSelectedEmbedderProvider(data.provider);
        setSelectedEmbedderModel(data.model_id);
      }
    } catch (error) {
      console.error("Error fetching active embedder:", error);
    }
  }, []);

  useEffect(() => {
    fetchActiveModel();
    fetchActiveEmbedder();
  }, [activeAiAgentId, fetchActiveModel, fetchActiveEmbedder]);

  // Derived Grouped Options
  const groupedChatModels = CHAT_MODELS.reduce((acc, model) => {
    if (!acc[model.provider]) acc[model.provider] = [];
    acc[model.provider].push(model);
    return acc;
  }, {});

  const groupedVisionModels = VISION_MODELS.reduce((acc, model) => {
    if (!acc[model.provider]) acc[model.provider] = [];
    acc[model.provider].push(model);
    return acc;
  }, {});

  const groupedEmbedders = EMBEDDERS.reduce((acc, embedder) => {
    if (!acc[embedder.provider]) acc[embedder.provider] = [];
    acc[embedder.provider].push(embedder);
    return acc;
  }, {});

  const handleSaveChatModel = async (e) => {
    e.preventDefault();
    if (!selectedChatProvider || !selectedChatModel) return;

    setChatLoading(true);
    try {
      await ApiClient.request("/models/save-config", {
        method: "POST",
        body: JSON.stringify({
          provider: selectedChatProvider,
          model_id: selectedChatModel,
          api_key: UNCHANGED_KEY,
          vision_provider: selectedVisionProvider,
          vision_model_id: selectedVisionModel,
          vision_api_key: UNCHANGED_KEY,
        }),
      });

      showToast("Chat model configuration saved successfully!", "success");
    } catch (error) {
      console.error("Error saving chat model:", error);
      showToast("Failed to save chat model configuration", "error");
    } finally {
      setChatLoading(false);
    }
  };

  const handleSaveVisionModel = async (e) => {
    e.preventDefault();
    if (!selectedVisionProvider || !selectedVisionModel) return;

    setVisionLoading(true);
    try {
      await ApiClient.request("/models/save-config", {
        method: "POST",
        body: JSON.stringify({
          provider: selectedChatProvider,
          model_id: selectedChatModel,
          api_key: UNCHANGED_KEY,
          vision_provider: selectedVisionProvider,
          vision_model_id: selectedVisionModel,
          vision_api_key: UNCHANGED_KEY,
        }),
      });

      showToast("Vision model configuration saved successfully!", "success");
    } catch (error) {
      console.error("Error saving vision model:", error);
      showToast("Failed to save vision model configuration", "error");
    } finally {
      setVisionLoading(false);
    }
  };

  const handleSaveEmbedder = async (e) => {
    e.preventDefault();
    if (!selectedEmbedderProvider || !selectedEmbedderModel)
      return;

    setEmbedderLoading(true);
    try {
      await ApiClient.request("/embedder/save-config", {
        method: "POST",
        body: JSON.stringify({
          provider: selectedEmbedderProvider,
          model_id: selectedEmbedderModel,
          api_key: UNCHANGED_KEY,
        }),
      });

      showToast("Embedder configuration saved successfully!", "success");
    } catch (error) {
      console.error("Error saving embedder:", error);
      showToast("Failed to save embedder configuration", "error");
    } finally {
      setEmbedderLoading(false);
    }
  };

  const getProviderName = (provider) => {
    const names = {
      openai: "OpenAI",
      anthropic: "Anthropic",
      google: "Google",
      cohere: "Cohere",
      fireworks: "Fireworks",
      together: "Together",
      nebius: "Nebius",
      mistral: "Mistral",
      jina: "Jina",
      meta: "Meta",
    };
    return names[provider] || provider;
  };

  const getChatModelsForProvider = (provider) => {
    return groupedChatModels[provider] || [];
  };

  const getEmbeddersForProvider = (provider) => {
    return groupedEmbedders[provider] || [];
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start">
      <div className="w-full max-w-6xl space-y-12">
        <PageHeader
          title="Model Engine"
          description="Configure the neural intelligence that powers your assistant's reasoning and memory."
        />

        <div className="bg-white rounded-[40px] p-2 shadow-xl shadow-gray-100/50 border border-gray-100">
          {/* Tabs Navigation */}
          <div className="flex justify-center p-1.5 gap-2 bg-white/50 backdrop-blur-md rounded-2xl mb-8 w-fit mx-auto shadow-sm border border-gray-100/50">
            <button
              onClick={() => setActiveTab("chat-models")}
              className={`px-8 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === "chat-models"
                  ? "bg-gray-900 text-white shadow-lg shadow-gray-200"
                  : "text-gray-400 hover:text-gray-900 hover:bg-white"
                }`}
            >
              <Bot size={14} />
              Chat Intelligence
            </button>
            <button
              onClick={() => setActiveTab("vision-models")}
              className={`px-8 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === "vision-models"
                  ? "bg-gray-900 text-white shadow-lg shadow-gray-200"
                  : "text-gray-400 hover:text-gray-900 hover:bg-white"
                }`}
            >
              <Eye size={14} />
              Vision Intelligence
            </button>
            <button
              onClick={() => setActiveTab("embedders")}
              className={`px-8 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === "embedders"
                  ? "bg-gray-900 text-white shadow-lg shadow-gray-200"
                  : "text-gray-400 hover:text-gray-900 hover:bg-white"
                }`}
            >
              <Database size={14} />
              Vector Embedders
            </button>
          </div>

          <div className="p-10 lg:p-14">
            {/* Chat Models Tab */}
            {activeTab === "chat-models" ? (
              <div className="animate-slideIn space-y-8">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white shadow-md shadow-gray-200/50">
                      <Bot size={18} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 tracking-tight">Chat Model</h2>
                      <p className="text-gray-400 font-semibold text-[11px] leading-none mt-0.5">Select the core LLM for real-time inference</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSaveChatModel} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2 block">
                        Provider
                      </label>
                      <CustomDropdown
                        value={selectedChatProvider}
                        onChange={(value) => {
                          setSelectedChatProvider(value);
                          setSelectedChatModel("");
                        }}
                        options={Object.keys(groupedChatModels).map(
                          (provider) => ({
                            value: provider,
                            label: getProviderName(provider),
                          }),
                        )}
                        placeholder="Select AI Provider"
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2 block">
                        Model Identity
                      </label>
                      <CustomDropdown
                        value={selectedChatModel}
                        onChange={setSelectedChatModel}
                        options={getChatModelsForProvider(
                          selectedChatProvider,
                        ).map((model) => ({
                          value: model.value,
                          label: model.label,
                        }))}
                        placeholder="Select Reasoning Model"
                        disabled={!selectedChatProvider}
                      />
                    </div>
                  </div>

                  <div className="pt-4 flex justify-center">
                    <button
                      type="submit"
                      disabled={
                        chatLoading ||
                        !selectedChatProvider ||
                        !selectedChatModel
                      }
                      className="w-fit px-10 py-2.5 bg-gray-900 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-800 transition-all shadow-lg shadow-gray-200 active:scale-95 disabled:opacity-30 flex items-center justify-center gap-3"
                    >
                      {chatLoading ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div> : <Save size={16} />}
                      {chatLoading ? "Propagating Changes..." : "Deploy Configuration"}
                    </button>
                  </div>
                </form>
              </div>
            ) : activeTab === "vision-models" ? (
              /* Vision Models Tab */
              <div className="animate-slideIn space-y-8">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white shadow-md shadow-gray-200/50">
                      <Eye size={18} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 tracking-tight">Vision Model</h2>
                      <p className="text-gray-400 font-semibold text-[11px] leading-none mt-0.5">Select the vision model for image/diagram captioning</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSaveVisionModel} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2 block">
                        Provider
                      </label>
                      <CustomDropdown
                        value={selectedVisionProvider}
                        onChange={(value) => {
                          setSelectedVisionProvider(value);
                          setSelectedVisionModel("");
                        }}
                        options={Object.keys(groupedVisionModels).map(
                          (provider) => ({
                            value: provider,
                            label: getProviderName(provider),
                          }),
                        )}
                        placeholder="Select AI Provider"
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2 block">
                        Model Identity
                      </label>
                      <CustomDropdown
                        value={selectedVisionModel}
                        onChange={setSelectedVisionModel}
                        options={(groupedVisionModels[selectedVisionProvider] || []).map((model) => ({
                          value: model.value,
                          label: model.label,
                        }))}
                        placeholder="Select Vision Model"
                        disabled={!selectedVisionProvider}
                      />
                    </div>
                  </div>

                  <div className="pt-4 flex justify-center">
                    <button
                      type="submit"
                      disabled={
                        visionLoading ||
                        !selectedVisionProvider ||
                        !selectedVisionModel
                      }
                      className="w-fit px-10 py-2.5 bg-gray-900 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-800 transition-all shadow-lg shadow-gray-200 active:scale-95 disabled:opacity-30 flex items-center justify-center gap-3"
                    >
                      {visionLoading ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div> : <Save size={16} />}
                      {visionLoading ? "Propagating Changes..." : "Deploy Vision Configuration"}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              /* Embedders Tab */
              <div className="animate-slideIn space-y-8">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white shadow-md shadow-gray-200/50">
                      <Database size={18} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 tracking-tight">Memory Engine</h2>
                      <p className="text-gray-400 font-semibold text-[11px] leading-none mt-0.5">Configure embedding vectors for context retrieval</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSaveEmbedder} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2 block">
                        Vector Provider
                      </label>
                      <CustomDropdown
                        value={selectedEmbedderProvider}
                        onChange={(value) => {
                          setSelectedEmbedderProvider(value);
                          setSelectedEmbedderModel("");
                        }}
                        options={Object.keys(groupedEmbedders).map(
                          (provider) => ({
                            value: provider,
                            label: getProviderName(provider),
                          }),
                        )}
                        placeholder="Select Embedder"
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-2 block">
                        Vector Model
                      </label>
                      <CustomDropdown
                        value={selectedEmbedderModel}
                        onChange={setSelectedEmbedderModel}
                        options={getEmbeddersForProvider(
                          selectedEmbedderProvider,
                        ).map((embedder) => ({
                          value: embedder.value,
                          label: embedder.label,
                        }))}
                        placeholder="Select Vector Model"
                        disabled={!selectedEmbedderProvider}
                      />
                    </div>
                  </div>

                  <div className="pt-4 flex justify-center">
                    <button
                      type="submit"
                      disabled={
                        embedderLoading ||
                        !selectedEmbedderProvider ||
                        !selectedEmbedderModel
                      }
                      className="w-fit mx-auto px-10 py-2.5 bg-gray-900 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-800 transition-all shadow-lg shadow-gray-200 active:scale-95 disabled:opacity-30 flex items-center justify-center gap-3"
                    >
                      {embedderLoading ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div> : <Save size={16} />}
                      {embedderLoading ? "Synchronizing..." : "Save Vector Config"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ModelManagement;
